import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Coins, Sparkle, Clock, Star, Heart, CalendarPlus } from "@phosphor-icons/react";
import { Capacitor } from "@capacitor/core";
import { PageShell } from "../components/Layout";
import PlaceAutocompleteInput from "../components/PlaceAutocompleteInput";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const tone = (s) => (s >= 90 ? "#10B981" : s >= 75 ? "#84CC16" : s >= 55 ? "#F59E0B" : s >= 35 ? "#FB923C" : "#94A3B8");
const fmt = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
const inputCls = "mt-1 w-full rounded-xl border border-amber-200 bg-[#FFFBF3] px-3 py-2.5 text-sm";

function WindowCard({ w, i }) {
  return (
    <article className="rounded-2xl border p-5" style={{ borderColor: `${tone(w.score)}66`, background: `${tone(w.score)}14` }} data-testid={`lottery-window-${i}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: tone(w.score) }}>#{i + 1} · {w.weekday}{w.peak ? " · PEAK" : ""}</div>
          <div className="font-serif-display text-2xl mt-0.5">{fmt(w.date)}</div>
          <div className="flex items-center gap-1.5 text-sm mt-1 opacity-85" data-testid={`lottery-window-${i}-time`}><Clock size={14} /> {w.from} → {w.to} · {w.hours}h</div>
        </div>
        <div className="rounded-xl px-3 py-2 text-center text-white" style={{ background: tone(w.score) }}>
          <div className="text-xl font-bold leading-none" data-testid={`lottery-window-${i}-score`}>{w.score}</div>
          <div className="text-[9px] uppercase tracking-wider">score</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-3 text-[11px]">
        <span className="px-2 py-0.5 rounded-full border border-amber-400/60 text-amber-500 font-semibold">{w.nakshatra} · pada {w.padas.join(", ")}</span>
        <span className="px-2 py-0.5 rounded-full border border-sky-400/50 text-sky-500">Moon in {w.moon_sign}</span>
        <span className="px-2 py-0.5 rounded-full border border-pink-400/50 text-pink-500">Venus in {w.venus_sign}</span>
      </div>
      <p className="text-[12px] mt-2 opacity-80">{w.note}</p>
      {w.venus_links.map((l) => (
        <p key={l} className="text-[12px] mt-1 text-pink-500 flex gap-1.5"><Heart size={13} weight="fill" className="mt-0.5 shrink-0" /> {l} (Venus boost)</p>
      ))}
    </article>
  );
}

function AddToCalendar({ form, data }) {
  const [minScore, setMinScore] = useState(60);
  const [busy, setBusy] = useState(false);
  const count = data.windows.filter((w) => w.score >= minScore).length;
  const add = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/lottery-dates/ics", { ...form, min_score: minScore });
      const url = `${process.env.REACT_APP_BACKEND_URL}${r.data.path}`;
      if (Capacitor.isNativePlatform()) {
        const { Browser } = await import("@capacitor/browser");
        await Browser.open({ url, presentationStyle: "popover" });
      } else {
        window.location.href = url;
      }
      toast.success(`${r.data.events} events ready. Tap "Add All" in Calendar.`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not create the calendar file.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-2xl border border-amber-300/40 p-4 sm:p-5 flex flex-wrap items-center gap-3" data-testid="lottery-calendar">
      <CalendarPlus size={22} weight="duotone" className="text-amber-500" />
      <div className="flex-1 min-w-[200px]">
        <div className="text-sm font-semibold">Add to iPhone / Google / Outlook calendar</div>
        <div className="text-[11px] opacity-70">Every window in the range goes in as an event with its exact time, plus a reminder 30 minutes before.</div>
      </div>
      <select value={minScore} onChange={(e) => setMinScore(Number(e.target.value))} className="rounded-xl border border-amber-300/50 bg-transparent px-3 py-2 text-sm" data-testid="lottery-calendar-min">
        <option value={0}>All windows</option>
        <option value={60}>Score 60+</option>
        <option value={80}>Score 80+</option>
        <option value={90}>Score 90+ only</option>
      </select>
      <button type="button" onClick={add} disabled={busy || !count} className="btn-primary rounded-full px-5 py-2.5 text-sm disabled:opacity-60" data-testid="lottery-calendar-btn">
        {busy ? "Preparing…" : `Add ${count} event${count === 1 ? "" : "s"}`}
      </button>
    </div>
  );
}

function DayStrip({ days }) {
  return (
    <div className="flex gap-1.5 overflow-x-auto pb-2" data-testid="lottery-day-strip">
      {days.map((d) => (
        <div key={d.date} title={d.best || "No lottery nakshatra"} className="shrink-0 w-[64px] rounded-xl border border-amber-300/30 p-2 text-center" data-testid={`lottery-day-${d.date}`}>
          <div className="text-[10px] opacity-60">{d.weekday.slice(0, 3)}</div>
          <div className="text-sm font-semibold">{d.date.slice(8)}/{d.date.slice(5, 7)}</div>
          <div className="mt-1 h-1.5 rounded-full" style={{ background: d.best_score ? tone(d.best_score) : "rgba(148,163,184,0.25)" }} />
          <div className="text-[11px] mt-1 font-bold">{d.best_score || "—"}</div>
        </div>
      ))}
    </div>
  );
}

export default function AdminLotteryDatesPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const today = new Date();
  const [form, setForm] = useState({ from_date: ymd(today), to_date: ymd(new Date(today.getTime() + 30 * 864e5)), place: "" });
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!authLoading && !user?.is_super_admin) navigate("/");
  }, [user, authLoading, navigate]);

  const run = async (e) => {
    e.preventDefault();
    if (!form.place.trim()) return toast.error("Enter the city where you'll buy the ticket.");
    setBusy(true);
    setData(null);
    try {
      const r = await api.post("/admin/lottery-dates", form);
      setData(r.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not compute lottery dates.");
    } finally {
      setBusy(false);
    }
  };

  if (authLoading || !user?.is_super_admin) return <PageShell><div className="p-10 text-center text-muted">Loading…</div></PageShell>;

  return (
    <PageShell>
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20" data-testid="lottery-dates-page">
        <div className="label-eyebrow text-muted flex items-center gap-2"><Coins size={14} weight="duotone" /> Super-admin · Moon nakshatra timing</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light leading-tight mt-3">Lottery Purchase <span className="text-gold">Date Calculator</span></h1>
        <p className="text-sm text-muted mt-3 max-w-3xl">Checks the transit Moon every 5 minutes for the lottery nakshatras: Purvashadha is the greatest, then Dhanishta and Mula, then Uttarabhadrapada (its pada 2 is the most powerful), then Swati, Ardra, Revati, Ashlesha, Shatabhisha, Chitra and Mrigashira. Venus conjunct the Moon, Venus 7th from it, or the Moon's sign lord in Taurus or Libra adds a boost.</p>

        <form onSubmit={run} className="rounded-2xl bg-white border border-amber-200/70 p-5 sm:p-6 mt-8 grid sm:grid-cols-[1fr_1fr_1.4fr_auto] gap-3 items-end" data-testid="lottery-form">
          <label className="text-xs font-semibold text-[#3F2D1B]/70">From<input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} className={inputCls} data-testid="lottery-from" /></label>
          <label className="text-xs font-semibold text-[#3F2D1B]/70">To (max 120 days)<input type="date" value={form.to_date} min={form.from_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} className={inputCls} data-testid="lottery-to" /></label>
          <label className="text-xs font-semibold text-[#3F2D1B]/70">Purchase city
            <div className="mt-1"><PlaceAutocompleteInput value={form.place} onChange={(v) => setForm({ ...form, place: v })} onPick={({ name }) => setForm({ ...form, place: name })} placeholder="Mumbai, India" testId="lottery-place" /></div>
          </label>
          <button type="submit" disabled={busy} className="btn-primary rounded-full px-6 py-3 text-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid="lottery-submit">
            <Sparkle size={15} weight="fill" /> {busy ? "Scanning…" : "Find best dates"}
          </button>
        </form>

        {data && (
          <div className="space-y-8 mt-10" data-testid="lottery-results">
            <p className="text-xs text-muted" data-testid="lottery-meta">{data.windows.length} lottery-nakshatra windows found between {fmt(data.from_date)} and {fmt(data.to_date)} · times in {data.timezone} for {data.place}</p>
            <AddToCalendar form={form} data={data} />
            <DayStrip days={data.days} />
            <div>
              <h2 className="font-serif-display text-2xl flex items-center gap-2"><Star size={20} weight="duotone" className="text-amber-500" /> Best windows to buy</h2>
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
                {data.top.map((w, i) => <WindowCard key={w.start} w={w} i={i} />)}
              </div>
              {data.top.length === 0 && <p className="text-sm text-muted mt-3">No lottery nakshatra in this range. Try a wider range.</p>}
            </div>
            <details className="rounded-2xl border border-amber-300/30 p-4" data-testid="lottery-all-windows">
              <summary className="cursor-pointer text-sm font-semibold">All {data.windows.length} windows in date order</summary>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead><tr className="text-left opacity-60 uppercase text-[10px] tracking-wider"><th className="py-1.5">Start</th><th>End</th><th>Nakshatra</th><th>Pada</th><th>Venus link</th><th>Score</th></tr></thead>
                  <tbody>
                    {data.windows.map((w) => (
                      <tr key={w.start} className="border-t border-amber-300/20">
                        <td className="py-1.5 pr-2">{w.start}</td><td className="pr-2">{w.end}</td><td className="pr-2">{w.nakshatra}</td>
                        <td className="pr-2">{w.padas.join(", ")}</td><td className="pr-2">{w.venus_links.length ? "Yes" : "—"}</td>
                        <td className="font-bold" style={{ color: tone(w.score) }}>{w.score}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </div>
        )}
      </div>
    </PageShell>
  );
}
